import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import {
  getStory,
  listStories,
  type StoryHeroThumb,
  type StoryListItem,
} from '../lib/stories';
import {
  getStepLabelKey,
  getStepPct,
  IN_PROGRESS_STATUSES,
  type StoryStatus,
} from '../lib/storyStatus';
import { useStoryProgress } from '../lib/useStoryProgress';
import './StoryProgress.css';

type TrackedStory = { id: number; heroes: StoryHeroThumb[] };

// Floating in-flight story notifier. Polls /api/stories/?status=...
// once on mount, then renders a per-story toast subscribed via SSE.
// Mounted at the App level so Profile/Heroes show it; the Stories page passes
// `disabled` because it renders inline progress bars instead.
export function StoryProgress({ disabled = false }: { disabled?: boolean } = {}) {
  const [stories, setStories] = useState<TrackedStory[] | null>(null);

  useEffect(() => {
    if (disabled) return;
    let cancelled = false;
    listStories(IN_PROGRESS_STATUSES.join(','))
      .then((items: StoryListItem[]) => {
        if (cancelled) return;
        setStories(items.map((s) => ({ id: s.id, heroes: s.heroes })));
      })
      .catch((err: unknown) => {
        // Anonymous users (401) silently get no notifier.
        if (err instanceof ApiError && err.status === 401) {
          if (!cancelled) setStories([]);
          return;
        }
        if (!cancelled) setStories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  // Public API for other components to register newly-submitted stories
  // (e.g. the Stories page after createStory). Wired via the custom event below.
  useEffect(() => {
    if (disabled) return;
    function onSubmitted(event: Event) {
      const detail = (event as CustomEvent<{ storyId: number; heroes?: StoryHeroThumb[] }>).detail;
      if (!detail) return;
      const entry: TrackedStory = { id: detail.storyId, heroes: detail.heroes ?? [] };
      setStories((prev) => (prev ? [...prev, entry] : [entry]));
    }
    window.addEventListener('kinotale:story-submitted', onSubmitted);
    return () => window.removeEventListener('kinotale:story-submitted', onSubmitted);
  }, [disabled]);

  if (disabled || !stories || stories.length === 0) return null;

  return (
    <div className="global-progress">
      {stories.map((s) => (
        <ProgressItem key={s.id} storyId={s.id} heroes={s.heroes} />
      ))}
    </div>
  );
}

function ProgressItem({
  storyId,
  heroes,
}: {
  storyId: number;
  heroes: StoryHeroThumb[];
}) {
  const { t } = useTranslation();
  const event = useStoryProgress(storyId);
  const [terminal, setTerminal] = useState<
    | null
    | { kind: 'done'; title: string }
    | { kind: 'failed'; message: string }
  >(null);
  const [hidden, setHidden] = useState(false);

  // When SSE reports complete, fetch title for the toast.
  useEffect(() => {
    if (event?.status !== 'complete') return;
    let cancelled = false;
    getStory(storyId)
      .then((s) => {
        if (!cancelled) setTerminal({ kind: 'done', title: s.title });
      })
      .catch(() => {
        if (!cancelled) setTerminal({ kind: 'done', title: t('stories.yourStoryFallback') });
      });
    return () => {
      cancelled = true;
    };
  }, [event?.status, storyId, t]);

  // Failure → show error toast.
  useEffect(() => {
    if (event?.status !== 'failed') return;
    setTerminal({ kind: 'failed', message: event.detail ?? t('stories.somethingWentWrong') });
  }, [event?.status, event?.detail, t]);

  // Auto-dismiss after a short hold so the user sees the result.
  useEffect(() => {
    if (!terminal) return;
    const ms = terminal.kind === 'done' ? 8000 : 10000;
    const handle = window.setTimeout(() => setHidden(true), ms);
    return () => window.clearTimeout(handle);
  }, [terminal]);

  if (hidden) return null;

  if (terminal?.kind === 'done') {
    return (
      <div className="global-progress-item global-progress-item--done">
        <h4>
          <Trans
            i18nKey="stories.readyToast"
            values={{ title: terminal.title }}
            components={[<Link to={`/stories/${storyId}`} key="0" />]}
          />
        </h4>
      </div>
    );
  }
  if (terminal?.kind === 'failed') {
    return (
      <div className="global-progress-item global-progress-item--failed">
        <h4>{t('stories.failedToastHeading')}</h4>
        <div className="gp-label">{terminal.message}</div>
      </div>
    );
  }

  const status: StoryStatus = event?.status ?? 'pending';
  return (
    <div className="global-progress-item">
      <h4>{t('stories.generatingToast')}</h4>
      {heroes.length > 0 && (
        <div className="gp-heroes">
          {heroes.map((h) => (
            <img
              key={h.id}
              src={h.photo}
              alt={h.name}
              title={h.name}
              className="gp-hero-thumb"
            />
          ))}
        </div>
      )}
      <div className="gp-bar">
        <div className="gp-fill" style={{ width: `${getStepPct(status)}%` }} />
      </div>
      <div className="gp-label">{event?.detail || t(getStepLabelKey(status))}</div>
      <div className="gp-note">{t('stories.emailNotice')}</div>
    </div>
  );
}
